/**
 * Miro's floating bar above the selection: its colour (a swatch or any colour), its words' font,
 * size (typed, `#board-size`, or a step at a time), bold, italic, underline, strike, colour
 * (`#board-text-colour`) and alignment, a picture's crop, a shape's outline, an arrow's line and head; and with several items selected, lining them up,
 * spreading them out, grouping; front and back, duplicate. Every button is one undo
 * (`[data-style=<key>][data-value=<value>]`, `[data-board-action=<name>]`).
 *
 * Where the board makes screens (UI/UX), a frame also gets its device (`#board-device`), auto
 * layout (`#board-layout`, `#board-gap`, `#board-padding`, `#board-align-items`) and "Component";
 * corners (`#board-radius`), an input's hidden words and a checkbox's tick. In prototype mode the
 * bar holds only where a click leads (`#board-navigate`) and how (`#board-transition`), or for a
 * screen "Start here".
 *
 * It stays while words are being written: a press on it keeps the keyboard in the words (but in
 * its fields), so their look changes as they are typed.
 */
import { For, Show, createMemo, createSignal, onCleanup, type JSX } from "solid-js";
import { useApp } from "../../../app/context";
import { BOARD_COLOURS, bounds, type Alignment } from "../../../state/board";
import { DEVICES, FONT_NAMES, FONTS, TEXT_SIZES } from "../../../shared/elements";
import type { RunKey } from "../../../shared/richText";
import { hasRange, textEditor } from "./textEditor";
import { TRANSITIONS } from "../../../state/prototype";
import { itemShape } from "./BoardItem";
import { SHAPES } from "./look";

/** The bar, placed above the selection's bounds on screen. */
export function StyleBar(): JSX.Element {
  const { board, view, graph } = useApp();
  const link = () =>
    view.state.selection?.type === "link" ? view.state.selection.link : null;
  /** Where the bar goes: above the selection (or the arrow's middle). */
  const at = () => {
    const places = board.content().places;
    const l = link();
    const rects = l
      ? [places.get(l.from), places.get(l.to)].filter((p) => !!p)
      : board
          .selected()
          .flatMap((id) => (places.has(id) ? [places.get(id)!] : []));
    const b = bounds(rects);
    if (!b) return null;
    const c = board.camera();
    return {
      x: (b.x + b.w / 2) * c.k + c.x,
      // Above a frame's title, which stays clickable.
      y: Math.max(48, b.y * c.k + c.y - 14 - (!l && board.selected().some((id) => board.content().frames.some((f) => f.id === id)) ? 26 : 0)),
    };
  };
  /** What the first selected item is drawn as. */
  const shape = () => {
    const id = board.selected()[0];
    const node = id
      ? graph.state.graph.nodes.find((n) => n.id === id)
      : undefined;
    return node ? itemShape(node.kind) : "frame";
  };
  const value = (key: string) => {
    const l = link();
    if (l) return board.styleOf(l, key);
    const id = board.selected()[0];
    return id ? board.styleOf(id, key) : "";
  };
  const set = (key: string, v: string) =>
    void board.setStyle(key, value(key) === v ? "" : v);
  /** The key the colour swatches write: words' colour for free text, else the fill (an arrow's line). */
  const colourKey = () =>
    link() ? "stroke" : shape() === "text" ? "color" : "fill";
  /** The words being written in this item, if any. */
  const editor = () => {
    const e = textEditor();
    return e && e.id === board.selected()[0] ? e : null;
  };
  /** The words being written, when some of them are selected: the bar then changes only those. */
  const ranged = () => {
    const e = editor();
    return hasRange(e) ? e : null;
  };
  /** A key of the words' look: the selected words', else the item's. */
  const textValue = (key: RunKey) => {
    const e = ranged();
    return e ? (e.value(key) ?? "") : value(key);
  };
  /** Sets a key of the words' look: on the selected words, else on the whole item (the words' own values go). */
  const put = (key: RunKey, v: string) => {
    const e = ranged();
    if (e) return e.apply(key, v);
    const open = editor();
    // A highlight is only ever the words': all of them.
    if (open && key === "highlight") return open.apply(key, v, true);
    open?.clear(key);
    void board.setStyle(key, v);
  };
  /** Switches a key of the words' look on or off ({@link put}). */
  const toggle = (key: RunKey, v: string) => put(key, textValue(key) === v ? "" : v);
  // A memo: the size typed in is not written over while the graph changes for something else.
  const size = createMemo(() => Number(textValue("size")) || (shape() === "text" ? 18 : 16));
  /** The bar's width and the board's, so the bar stays inside the board. */
  const [widths, setWidths] = createSignal({ bar: 0, stage: 0 });
  let observer: ResizeObserver | undefined;
  onCleanup(() => observer?.disconnect());
  /** Watches the bar's size. */
  const measure = (el: HTMLDivElement) => {
    observer?.disconnect();
    const update = () => setWidths({ bar: el.offsetWidth, stage: el.parentElement?.clientWidth ?? 0 });
    observer = new ResizeObserver(update);
    observer.observe(el);
    queueMicrotask(update);
  };
  /** The bar's middle, kept so that the whole bar is on the board. */
  const left = (x: number) => {
    const { bar, stage } = widths();
    if (!bar || !stage) return x;
    const half = bar / 2 + 8;
    return stage < bar + 16 ? stage / 2 : Math.min(stage - half, Math.max(half, x));
  };
  const many = () => board.selected().length > 1;
  const aligns: { how: Alignment; label: string }[] = [
    { how: "left", label: "Line up left edges" },
    { how: "centre", label: "Line up centres" },
    { how: "right", label: "Line up right edges" },
    { how: "top", label: "Line up tops" },
    { how: "middle", label: "Line up middles" },
    { how: "bottom", label: "Line up bottoms" },
  ];
  return (
    <Show when={!board.busy() && at()}>
      {(p) => (
        <div
          ref={measure}
          class="board-stylebar"
          id="board-stylebar"
          style={{ left: `${left(p().x)}px`, top: `${p().y}px` }}
          onPointerDown={(e) => e.stopPropagation()}
          // The words being written keep the keyboard (a field of the bar takes it).
          onMouseDown={(e) => !(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) && e.preventDefault()}
          onDblClick={(e) => e.stopPropagation()}
        >
          <Show when={board.prototyping()}>
            <Show
              when={shape() !== "frame"}
              fallback={
                <Show
                  when={board
                    .screens()
                    .some((f) => f.id === board.selected()[0])}
                >
                  <button
                    type="button"
                    data-board-action="start"
                    aria-pressed={value("start") === "yes"}
                    onClick={() => void board.makeStart()}
                  >
                    ▶ Start here
                  </button>
                </Show>
              }
            >
              <label class="board-field">
                On click, go to
                <select
                  id="board-navigate"
                  value={value("navigate")}
                  onChange={(e) =>
                    void Promise.all(
                      board
                        .selected()
                        .map((id) => board.lead(id, e.currentTarget.value)),
                    )
                  }
                >
                  <option value="">nowhere</option>
                  <option value="back">the screen before</option>
                  <For each={board.screens()}>
                    {(f) => <option value={f.id}>{f.group.name}</option>}
                  </For>
                </select>
              </label>
              <Show when={value("navigate")}>
                <select
                  id="board-transition"
                  aria-label="Transition"
                  value={value("transition") || "instant"}
                  onChange={(e) =>
                    void board.setStyle(
                      "transition",
                      e.currentTarget.value === "instant"
                        ? ""
                        : e.currentTarget.value,
                    )
                  }
                >
                  <For each={TRANSITIONS}>
                    {(t) => <option value={t}>{t}</option>}
                  </For>
                </select>
              </Show>
            </Show>
          </Show>
          <Show when={!board.prototyping()}>
            <div class="board-swatches">
              <For each={BOARD_COLOURS}>
                {(colour) => (
                  <button
                    type="button"
                    class="swatch"
                    data-style={colourKey()}
                    data-value={colour}
                    aria-label={`Colour ${colour}`}
                    aria-pressed={value(colourKey()) === colour}
                    style={{ background: colour }}
                    onClick={() => set(colourKey(), colour)}
                  />
                )}
              </For>
              <input
                type="color"
                class="board-colour-pick"
                id="board-colour"
                aria-label="Any colour"
                title="Any colour"
                value={/^#[0-9a-f]{6}$/i.test(value(colourKey())) ? value(colourKey()) : "#ffe680"}
                onChange={(e) => void board.setStyle(colourKey(), e.currentTarget.value)}
              />
            </div>
            <Show when={shape() === "image"}>
              <button type="button" data-board-action="crop" title="Crop (or double-click the picture)" onClick={() => board.selected()[0] && board.startCrop(board.selected()[0]!)}>
                Crop
              </button>
              <Show when={value("crop")}>
                <button type="button" data-board-action="uncrop" title="Show the whole picture again" onClick={() => void board.uncrop()}>
                  Whole picture
                </button>
              </Show>
            </Show>
            <Show when={link()}>
              <For each={["solid", "dashed", "dotted"]}>
                {(line) => (
                  <button
                    type="button"
                    data-style="line"
                    data-value={line}
                    aria-pressed={(value("line") || "solid") === line}
                    onClick={() => set("line", line === "solid" ? "" : line)}
                  >
                    {line}
                  </button>
                )}
              </For>
              <button
                type="button"
                data-style="head"
                data-value="none"
                aria-pressed={value("head") === "none"}
                onClick={() => set("head", "none")}
              >
                no head
              </button>
            </Show>
            <Show when={!link()}>
              <Show
                when={
                  shape() !== "image" &&
                  shape() !== "frame" &&
                  shape() !== "instance"
                }
              >
                <div class="board-text-tools" role="group" aria-label="Words">
                  <select id="board-font" aria-label="Font" title="Font" value={textValue("font") || "sans"} onChange={(e) => put("font", e.currentTarget.value === "sans" ? "" : e.currentTarget.value)}>
                    <For each={Object.entries(FONT_NAMES)}>{([id, name]) => <option value={id} style={{ "font-family": FONTS[id] }}>{name}</option>}</For>
                  </select>
                  <div class="board-size-combo">
                    <button type="button" data-board-action="smaller" aria-label="Smaller words" title="Smaller" onClick={() => put("size", String(Math.max(TEXT_SIZES.min, size() - (size() > 12 ? 2 : 1))))}>
                      −
                    </button>
                    <input
                      class="board-size"
                      id="board-size"
                      type="number"
                      list="board-sizes"
                      min={TEXT_SIZES.min}
                      max={TEXT_SIZES.max}
                      aria-label="Size of the words"
                      title="Size (type any from 1 to 400)"
                      value={size()}
                      onChange={(e) => {
                        const n = Math.round(Number(e.currentTarget.value));
                        if (Number.isFinite(n) && n > 0) put("size", String(Math.min(TEXT_SIZES.max, Math.max(TEXT_SIZES.min, n))));
                        else e.currentTarget.value = String(size());
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        // The key itself goes nowhere: the words being written take the keyboard back, and would get a line break.
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                    />
                    <datalist id="board-sizes">
                      <For each={[10, 12, 14, 18, 24, 36, 48, 64, 80, 144, 288]}>{(n) => <option value={n} />}</For>
                    </datalist>
                    <button type="button" data-board-action="bigger" aria-label="Bigger words" title="Bigger" onClick={() => put("size", String(Math.min(TEXT_SIZES.max, size() + (size() >= 12 ? 2 : 1))))}>
                      +
                    </button>
                  </div>
                  <span class="board-sep" />
                  <button type="button" data-style="weight" data-value="bold" aria-pressed={textValue("weight") === "bold"} aria-label="Bold" title="Bold (Ctrl+B)" onClick={() => toggle("weight", "bold")}>
                    <b>B</b>
                  </button>
                  <button type="button" data-style="italic" data-value="yes" aria-pressed={textValue("italic") === "yes"} aria-label="Italic" title="Italic (Ctrl+I)" onClick={() => toggle("italic", "yes")}>
                    <i>I</i>
                  </button>
                  <button type="button" data-style="underline" data-value="yes" aria-pressed={textValue("underline") === "yes"} aria-label="Underline" title="Underline (Ctrl+U)" onClick={() => toggle("underline", "yes")}>
                    <u>U</u>
                  </button>
                  <button type="button" data-style="strike" data-value="yes" aria-pressed={textValue("strike") === "yes"} aria-label="Strike through" title="Strike through (Ctrl+Shift+X)" onClick={() => toggle("strike", "yes")}>
                    <s>S</s>
                  </button>
                  <span class="board-sep" />
                  <label class="board-text-colour" title="Colour of the words">
                    <span style={{ "border-bottom-color": textValue("color") || "#1d1b26" }}>A</span>
                    <input type="color" id="board-text-colour" aria-label="Colour of the words" value={/^#[0-9a-f]{6}$/i.test(textValue("color")) ? textValue("color") : "#1d1b26"} onChange={(e) => put("color", e.currentTarget.value)} />
                  </label>
                  <label class="board-text-colour board-highlight" title="Highlight the words">
                    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true">
                      <path d="M9.5 2.5l4 4-6 6H5.5l-2-2v-2zM3 14.5h10" />
                    </svg>
                    <span class="board-highlight-bar" style={{ background: textValue("highlight") || "transparent" }} />
                    <input type="color" id="board-highlight" aria-label="Highlight the words" value={/^#[0-9a-f]{6}$/i.test(textValue("highlight")) ? textValue("highlight") : "#fff3a3"} onChange={(e) => put("highlight", e.currentTarget.value)} />
                  </label>
                  <Show when={textValue("highlight")}>
                    <button type="button" data-board-action="no-highlight" aria-label="No highlight" title="No highlight" onClick={() => put("highlight", "")}>
                      ⌫
                    </button>
                  </Show>
                  <span class="board-sep" />
                  <For each={["left", "center", "right"]}>
                    {(a) => (
                      <button type="button" data-style="align" data-value={a} aria-pressed={value("align") === a} aria-label={`Words ${a}`} title={`Words ${a === "center" ? "in the middle" : `on the ${a}`}`} onClick={() => set("align", a)}>
                        <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                          <path d={a === "left" ? "M2 4h12M2 8h8M2 12h10" : a === "center" ? "M2 4h12M4 8h8M3 12h10" : "M2 4h12M6 8h8M4 12h10"} />
                        </svg>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
              <Show when={board.prototypes() && shape() === "frame"}>
                <select
                  id="board-device"
                  aria-label="Device"
                  value={value("device")}
                  onChange={(e) => void board.setDevice(e.currentTarget.value)}
                >
                  <option value="">free size</option>
                  <For each={Object.entries(DEVICES)}>
                    {([id, d]) => (
                      <option value={id}>{`${d.label} ${d.w}×${d.h}`}</option>
                    )}
                  </For>
                </select>
                <select
                  id="board-layout"
                  aria-label="Auto layout"
                  value={value("layout")}
                  onChange={(e) =>
                    void board.setStyle("layout", e.currentTarget.value)
                  }
                >
                  <option value="">no auto layout</option>
                  <option value="column">column ↓</option>
                  <option value="row">row →</option>
                </select>
                <Show when={value("layout")}>
                  <label class="board-field">
                    gap
                    <input
                      id="board-gap"
                      type="number"
                      min="0"
                      max="400"
                      value={value("gap") || "0"}
                      onChange={(e) =>
                        void board.setStyle(
                          "gap",
                          e.currentTarget.value === "0"
                            ? ""
                            : e.currentTarget.value,
                        )
                      }
                    />
                  </label>
                  <label class="board-field">
                    padding
                    <input
                      id="board-padding"
                      type="number"
                      min="0"
                      max="400"
                      value={value("padding") || "0"}
                      onChange={(e) =>
                        void board.setStyle(
                          "padding",
                          e.currentTarget.value === "0"
                            ? ""
                            : e.currentTarget.value,
                        )
                      }
                    />
                  </label>
                  <select
                    id="board-align-items"
                    aria-label="Line up across"
                    value={value("align") || "start"}
                    onChange={(e) =>
                      void board.setStyle(
                        "align",
                        e.currentTarget.value === "start"
                          ? ""
                          : e.currentTarget.value,
                      )
                    }
                  >
                    <option value="start">start</option>
                    <option value="center">centre</option>
                    <option value="end">end</option>
                    <option value="stretch">stretch</option>
                  </select>
                </Show>
                <button
                  type="button"
                  data-board-action="component"
                  aria-pressed={value("component") === "yes"}
                  title="A component: its instances follow every change made to it"
                  onClick={() => void board.toggleComponent()}
                >
                  ◇ Component
                </button>
              </Show>
              <Show
                when={
                  board.prototypes() &&
                  shape() !== "text" &&
                  shape() !== "note" &&
                  shape() !== "list" &&
                  shape() !== "checkbox"
                }
              >
                <select
                  id="board-radius"
                  aria-label="Corners"
                  value={value("radius")}
                  onChange={(e) =>
                    void board.setStyle("radius", e.currentTarget.value)
                  }
                >
                  <option value="">usual corners</option>
                  <For each={["0", "4", "8", "12", "24", "999"]}>
                    {(r) => (
                      <option value={r}>
                        {r === "999" ? "round" : `${r} px corners`}
                      </option>
                    )}
                  </For>
                </select>
              </Show>
              <Show when={shape() === "input"}>
                <button
                  type="button"
                  data-style="secret"
                  data-value="yes"
                  aria-pressed={value("secret") === "yes"}
                  title="Hide what is typed (a password)"
                  onClick={() => set("secret", "yes")}
                >
                  ••• hidden
                </button>
              </Show>
              <Show when={shape() === "checkbox"}>
                <button
                  type="button"
                  data-style="checked"
                  data-value="yes"
                  aria-pressed={value("checked") === "yes"}
                  title="Ticked at first"
                  onClick={() => set("checked", "yes")}
                >
                  ☑ ticked
                </button>
              </Show>
              <Show when={shape() === "shape"}>
                <select
                  id="board-shape"
                  aria-label="Shape"
                  value={value("shape") || "rectangle"}
                  onChange={(e) =>
                    void board.setStyle("shape", e.currentTarget.value)
                  }
                >
                  <For each={SHAPES}>
                    {(s) => <option value={s}>{s}</option>}
                  </For>
                </select>
              </Show>
              <Show when={many()}>
                <span class="board-sep" />
                <For each={aligns}>
                  {(a) => (
                    <button
                      type="button"
                      data-board-action={`align-${a.how}`}
                      aria-label={a.label}
                      title={a.label}
                      onClick={() => void board.align(a.how)}
                    >
                      {a.how === "left"
                        ? "⇤"
                        : a.how === "right"
                          ? "⇥"
                          : a.how === "top"
                            ? "⤒"
                            : a.how === "bottom"
                              ? "⤓"
                              : a.how === "centre"
                                ? "↔"
                                : "↕"}
                    </button>
                  )}
                </For>
                <button
                  type="button"
                  data-board-action="spread-x"
                  aria-label="Spread out across"
                  title="Spread out across (three or more)"
                  onClick={() => void board.distribute("x")}
                >
                  ⋯
                </button>
                <button
                  type="button"
                  data-board-action="spread-y"
                  aria-label="Spread out down"
                  title="Spread out down (three or more)"
                  onClick={() => void board.distribute("y")}
                >
                  ⋮
                </button>
                <button
                  type="button"
                  data-board-action="group"
                  title="Group (Ctrl+G)"
                  onClick={() => void board.group()}
                >
                  Group
                </button>
              </Show>
              <Show when={shape() !== "frame"}>
                <span class="board-sep" />
                <button
                  type="button"
                  data-board-action="front"
                  title="Bring to front (Ctrl+])"
                  onClick={() => void board.order("front")}
                >
                  Front
                </button>
                <button
                  type="button"
                  data-board-action="back"
                  title="Send to back (Ctrl+[)"
                  onClick={() => void board.order("back")}
                >
                  Back
                </button>
                <button
                  type="button"
                  data-board-action="duplicate"
                  title="Duplicate (Ctrl+D)"
                  onClick={() => void board.duplicate()}
                >
                  Duplicate
                </button>
              </Show>
            </Show>
          </Show>
        </div>
      )}
    </Show>
  );
}
